/**
 * NotificationsPage (CONTRACTS §6, spec §8.9) — in-app notification centre
 * for both roles.
 *
 * - per-type Dutch label with icon + text (never color-only);
 * - unread count badge, mark read / mark all read;
 * - deep links via AppNotification.deepLink → navigate;
 * - realtime refresh via subscribeMedicationRealtime when available, plus
 *   refresh on focus/visibility and a 30 s poll while the page is open —
 *   all with cleanup;
 * - calm empty state; server-authorized recipient (repository scopes by
 *   session, never by caller input).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusChip,
  useToast,
} from '@/components/ui'
import {
  AlertIcon,
  BellIcon,
  CheckIcon,
  PillIcon,
  SyncIcon,
  UsersIcon,
  VideoIcon,
} from '@/components/icons'
import { repositories, subscribeMedicationRealtime } from '@/services/repositories'
import { useAuth } from '@/features/auth'
import { errorMessage, formatDutchDateTime, type LoadStatus } from '@/features/medication'
import type { AppNotification, NotificationType } from '@/types'
import './notifications.css'

const POLL_INTERVAL_MS = 30_000

const TYPE_META: Record<NotificationType, { label: string; icon: (size: number) => ReactNode }> = {
  medication_scheduled: { label: 'Medicatie ingepland', icon: (size) => <PillIcon size={size} /> },
  medication_taken: { label: 'Medicatie ingenomen', icon: (size) => <CheckIcon size={size} /> },
  proof_uploaded: { label: 'Bewijsvideo geüpload', icon: (size) => <VideoIcon size={size} /> },
  proof_viewed: { label: 'Bewijsvideo bekeken', icon: (size) => <VideoIcon size={size} /> },
  caregiver_schedule_changed: { label: 'Schema gewijzigd', icon: (size) => <PillIcon size={size} /> },
  sync_error: { label: 'Synchronisatiefout', icon: (size) => <SyncIcon size={size} /> },
  link_request: { label: 'Koppelverzoek', icon: (size) => <UsersIcon size={size} /> },
}

function typeMeta(type: NotificationType): { label: string; icon: (size: number) => ReactNode } {
  return TYPE_META[type] ?? { label: 'Melding', icon: (size) => <AlertIcon size={size} /> }
}

export default function NotificationsPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { userId } = useAuth()

  const [state, setState] = useState<{ status: LoadStatus; notifications: AppNotification[]; error: string | null }>({
    status: 'loading',
    notifications: [],
    error: null,
  })
  const [markingAll, setMarkingAll] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!options.silent) {
      setState((prev) => ({ ...prev, status: prev.status === 'ready' ? 'ready' : 'loading', error: null }))
    }
    try {
      const notifications = await repositories.notifications.listMine(100)
      if (mountedRef.current) setState({ status: 'ready', notifications, error: null })
    } catch (error) {
      if (!mountedRef.current) return
      // Keep showing already-loaded data on a failed silent refresh.
      setState((prev) =>
        prev.status === 'ready'
          ? prev
          : { status: 'error', notifications: [], error: errorMessage(error) },
      )
    }
  }, [])

  // Initial load.
  useEffect(() => {
    void load()
  }, [load])

  // Realtime refresh when available; poll + focus/visibility as baseline.
  useEffect(() => {
    let unsubscribe: (() => void) | null = null
    if (userId) {
      try {
        unsubscribe = subscribeMedicationRealtime({
          clientId: userId,
          recipientId: userId,
          onEvent: (event) => {
            if (event.table === 'notifications') void load({ silent: true })
          },
        })
      } catch {
        // Realtime unavailable (e.g. cloud not configured) — polling covers it.
        unsubscribe = null
      }
    }
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load({ silent: true })
    }, POLL_INTERVAL_MS)
    const onFocus = () => void load({ silent: true })
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load({ silent: true })
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      unsubscribe?.()
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [userId, load])

  const unreadCount = useMemo(
    () => state.notifications.filter((notification) => notification.readAt === null).length,
    [state.notifications],
  )

  const open = async (notification: AppNotification) => {
    if (notification.readAt === null) {
      // Optimistic read-state; the repository write resolves after the server confirms.
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.map((candidate) =>
          candidate.id === notification.id
            ? { ...candidate, readAt: new Date().toISOString() }
            : candidate,
        ),
      }))
      try {
        await repositories.notifications.markRead(notification.id)
      } catch {
        void load({ silent: true })
      }
    }
    if (notification.deepLink) navigate(notification.deepLink)
  }

  const markAllRead = async () => {
    if (markingAll || unreadCount === 0) return
    setMarkingAll(true)
    try {
      await repositories.notifications.markAllRead()
      await load({ silent: true })
      showToast({ message: 'Alle meldingen zijn als gelezen gemarkeerd.', tone: 'success' })
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setMarkingAll(false)
    }
  }

  return (
    <div className="notifications-page">
      <header className="notifications-header">
        <div>
          <h1 className="notifications-title">
            Meldingen
            {unreadCount > 0 ? (
              <span className="notifications-badge" aria-label={`${unreadCount} ongelezen meldingen`}>
                {unreadCount}
              </span>
            ) : null}
          </h1>
          <p className="notifications-muted">
            {unreadCount === 0
              ? 'Je bent helemaal bij.'
              : unreadCount === 1
                ? '1 ongelezen melding.'
                : `${unreadCount} ongelezen meldingen.`}
          </p>
        </div>
        {unreadCount > 0 ? (
          <Button variant="ghost" size="sm" loading={markingAll} onClick={() => void markAllRead()}>
            Alles gelezen
          </Button>
        ) : null}
      </header>

      {state.status === 'loading' ? <Skeleton lines={4} /> : null}
      {state.status === 'unavailable' ? (
        <Card padding="md">
          <p className="notifications-muted">Cloud niet ingesteld. Meldingen kunnen nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {state.status === 'error' ? <ErrorState onRetry={() => void load()} /> : null}

      {state.status === 'ready' ? (
        state.notifications.length === 0 ? (
          <EmptyState
            icon={<BellIcon size={26} />}
            title="Geen meldingen"
            body="Rustig — er is nu niets dat je aandacht vraagt. Nieuwe meldingen verschijnen hier vanzelf."
          />
        ) : (
          <ul className="notifications-list" aria-label="Meldingen">
            {state.notifications.map((notification) => {
              const meta = typeMeta(notification.type)
              const unread = notification.readAt === null
              return (
                <li key={notification.id}>
                  <button
                    type="button"
                    className={`notifications-item${unread ? ' notifications-item--unread' : ''}`}
                    onClick={() => void open(notification)}
                    aria-label={`${meta.label}: ${notification.title}`}
                  >
                    <span className="notifications-item-icon" aria-hidden="true">
                      {meta.icon(20)}
                    </span>
                    <span className="notifications-item-text">
                      <span className="notifications-item-heading">
                        <span className="notifications-item-title">{notification.title}</span>
                        {unread ? <StatusChip status="info">Nieuw</StatusChip> : null}
                      </span>
                      {notification.body ? (
                        <span className="notifications-item-body">{notification.body}</span>
                      ) : null}
                      <span className="notifications-item-meta">
                        {meta.label} · {formatDutchDateTime(notification.createdAt)}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )
      ) : null}
    </div>
  )
}
